/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, Subject, catchError, switchMap, take, tap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from '../../services/toaster.service';
import { AuthService } from '../../services/auth.service';
import { responseStatus } from '../constants/responseStatus.const';
 import { UserRole } from '../constants/keys.constant';

@Injectable({
  providedIn: 'root'
})
export class GetInterceptor implements HttpInterceptor {
  /** Dedupes the unauthorized-page warning when several requests 401 at once at. */
  private isShowingUnauthorizedWarning = false;
  /** Dedupes the toast + home redirect when several requests 403 at once. */
  private isShowingForbiddenWarning = false;

  /** True while a single access-token refresh call is in flight — guards against firing N refresh calls for N concurrent 401s. */
  private isRefreshing = false;
  /** One-shot broadcast per refresh cycle: true = refreshed ok (waiters retry), false = refresh failed (waiters fall through to logout). */
  private refreshResult$ = new Subject<boolean>();

  constructor(
    private _toastrService: ToasterService,
    private router: Router,
    private translateService: TranslateService,
    private authService: AuthService
  ) { }

  /** Student pathway / journey HTTP APIs (PathwayService) — only these show envelope `message` toasts. */
  private isPathwaysV2ApiUrl(url: string): boolean {
    const lower = url.toLowerCase();
    if (lower.includes('mini-course/update-progress') || lower.includes('updatescormprogress')) {
      return false;
    }
    return lower.includes('/pathways');
  }

  /** APIs that handle their own inline error UI — suppress the global toaster for these. */
  private isSilentErrorUrl(url: string): boolean {
    const lower = url.toLowerCase();
    return lower.includes('knowledgebankworksheets/')
      || lower.includes('knowledgebankminiquizs/')
      || lower.includes('knowledgebanklessons/')
      || lower.includes('knowledgebanklessonplans/')
      || lower.endsWith('learningsessions') // topic-form shows popup for 422/502; handles all errors inline
      || lower.includes('useractivity/heartbeat'); // background active-time tracker — retries silently
  }

  /** 401 with a still-valid session (i.e. access denied, not a token expiry) → send to the unauthorized page. */
  private isUnauthorizedNotExpired(error: HttpErrorResponse): boolean {
    const isSessionExpired = (error?.error as { isSessionExpired?: unknown })?.isSessionExpired === true;
    return error['status'] === responseStatus.UNAUTHORIZED && isSessionExpired === false;
  }

  /** 401 that represents a genuinely expired access token — the case we can recover from via refresh. */
  private isTokenExpired401(error: HttpErrorResponse): boolean {
    return error['status'] === responseStatus.UNAUTHORIZED && !this.isUnauthorizedNotExpired(error);
  }

  /** Pre-auth / refresh endpoints — never attempt a token refresh for these (no session yet, or would recurse). */
  private isAuthBypassUrl(url: string): boolean {
    const lower = url.toLowerCase();
    return lower.includes('user/login')
      || lower.includes('user/refresh-token')
      || lower.includes('user/verifyotp')
      || lower.includes('user/sociallogin');
  }

  /**
   * True while the user is sitting on the public activation/set-password screen — reached
   * straight from an email link, before any login. A 401/403 here (e.g. from unrelated
   * background calls like `settings`, or from an invalid/expired activation token) must
   * never clear storage or navigate away: there is no session to lose, and the component
   * itself renders the invalid/expired-token state from the API response body.
   */
  private isPublicActivationRoute(): boolean {
    return this.router.url.split('?')[0].split('#')[0].includes('/auth/set-password');
  }

  /** APIs that show their own confirmation dialog for 422/423 — suppress the global toaster only for those codes. */
  private isValidationDialogUrl(url: string): boolean {
    const lower = url.toLowerCase();
    return lower.includes('knowledgebank/upload-file')
      || lower.endsWith('knowledgebank/generate')
      || lower.endsWith('knowledgebank/create');
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.handle(req, next, false);
  }

  private handle(
    req: HttpRequest<any>,
    next: HttpHandler,
    isRetry: boolean
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap({
        next: (event: HttpEvent<any>) => {
          if (!(event instanceof HttpResponse) || !event.ok) {
            return;
          }
          const body = event.body;
          if (body === null || body === undefined || typeof body !== 'object' || Array.isArray(body)) {
            return;
          }
          const envelope = body as { message?: unknown; statusCode?: unknown };
          const rawMsg = envelope.message;
          if (typeof rawMsg !== 'string') {
            return;
          }
          const msg = rawMsg.trim();
          if (!msg.length) {
            return;
          }
          const method = req.method.toUpperCase();
          const isMutation =
          method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
          if (!isMutation) {
            return;
          }
          if (!this.isPathwaysV2ApiUrl(req.url)) {
            return;
          }
          const code = envelope.statusCode;
          if (typeof code === 'number' && code >= 400) {
            this._toastrService.errToastr(msg);
            return;
          }
          this._toastrService.successToastr(msg);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        if (this.isPublicActivationRoute()) {
          this.reportGenericError(error, req);
          return throwError(() => error);
        }

        //If status 401 but not an expired session, redirect to unauthorized page
        if (this.isUnauthorizedNotExpired(error)) {
          this.handleUnauthorizedNotExpired();
          return EMPTY;
        }

        // Genuinely expired access token (not already a retry, not a pre-auth/refresh endpoint) — refresh and retry.
        if (!isRetry && this.isTokenExpired401(error) && !this.isAuthBypassUrl(req.url)) {
          return this.handleTokenExpiry(req, next);
        }

        this.reportGenericError(error, req);
        return throwError(() => error);
      })
    );
  }

  private handleUnauthorizedNotExpired(): void {
    if (!this.isShowingUnauthorizedWarning) {
      this.isShowingUnauthorizedWarning = true;
      this._toastrService.warningToastr(this.translateService.instant('UnauthorizedPageAccess'));
      const role = this.authService.currentProfileDetails?.role;
      const isStudentOrParent = role === UserRole.STUDENT || role === UserRole.PARENT;
      this.router.navigate([isStudentOrParent ? '/unauthorized' : '/tutor/unauthorized']);
      setTimeout(() => { this.isShowingUnauthorizedWarning = false; }, 3000);
    }
  }

  /**
   * Ensures only one refresh-token call is in flight at a time. Every 401 that lands here — whether it's the
   * one that kicks off the refresh, or one that arrives while a refresh is already running — subscribes to the
   * same `refreshResult$` broadcast and retries (or falls through to logout) with its own original `req`.
   */
  private handleTokenExpiry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // No refresh token stored (storage was cleared by a logout / another tab / an earlier 401-403, or the
    // session was started without one) — nothing to refresh with. Calling the API would only send
    // `refreshToken: null` and pop a "must be a string" error, so end the session quietly instead.
    if (!this.isRefreshing && !this.authService.getRefreshToken()) {
      this.endSessionSilently();
      return EMPTY;
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.authService.refreshToken().subscribe({
        next: (res) => {
          this.authService.storeTokens(res.data.accessToken, res.data.refreshToken);
          this.isRefreshing = false;
          this.refreshResult$.next(true);
        },
        error: () => {
          this.isRefreshing = false;
          this.refreshResult$.next(false);
        }
      });
    }

    return this.refreshResult$.pipe(
      take(1),
      switchMap((refreshed) => {
        if (refreshed) {
          return this.handle(req, next, true);
        }
        this.handleLogout();
        return EMPTY;
      })
    );
  }

  private handleLogout(): void {
    this._toastrService.errToastr(this.translateService.instant('networkConnection'));
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  private handleForbidden(error: HttpErrorResponse): void {
    if (this.isShowingForbiddenWarning) {
      return;
    }
    this.isShowingForbiddenWarning = true;
    const apiMessage = (error?.error as { message?: unknown })?.message;
    this._toastrService.errToastr(
      typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage
        : this.translateService.instant('UnauthorizedPageAccess')
    );
    const role = this.authService.currentProfileDetails?.role;
    const homeRoute = role === UserRole.TUTOR || role === UserRole.COUNSELOR ? '/tutor/dashboard' : '/dashboard';
    if (!this.router.url.startsWith(homeRoute)) {
      this.router.navigate([homeRoute]);
    }
    setTimeout(() => { this.isShowingForbiddenWarning = false; }, 3000);
  }

  private endSessionSilently(): void {
    this.authService.stopRefreshTimer();
    this.authService.clearTokens();
    if (!this.router.url.startsWith('/auth/login')) {
      this.router.navigate(['/auth/login']);
    }
  }

  private reportGenericError(error: HttpErrorResponse, req: HttpRequest<any>): void {
    if (this.isSilentErrorUrl(req.url)) {
      return;
    }
    if ((error['status'] === 422 || error['status'] === 423) && this.isValidationDialogUrl(req.url)) {
      return;
    }
    const apiMessage =
      error?.error &&
        typeof error.error === 'object' &&
        error.error !== null &&
        'message' in error.error &&
        typeof (error.error as { message?: unknown }).message === 'string'
        ? String((error.error as { message: string }).message).trim()
        : '';
    const isFileUpload = req.url.includes('/media/uploadFile');
    const uploadFallback = (): string => apiMessage || this.translateService.instant('FileUploadFailed');
    switch (error['status']) {
      case responseStatus.BADREQUEST: {
        this._toastrService.errToastr(
          isFileUpload ? uploadFallback() : apiMessage || error['error']['message']
        );
        break;
      }
      case responseStatus.NOTFOUND: {
        this._toastrService.errToastr(
          apiMessage || this.translateService.instant('networkConnection')
        );
        break;
      }
      case responseStatus.FORBIDDEN: {
        // 403 = logged in but not allowed to access this resource — keep the session, send the user home.
        this.handleForbidden(error);
        break;
      }
      case responseStatus.UNAUTHORIZED: {
        if (this.isPublicActivationRoute()) {
          // Let the set-password component surface the API's own error message
          // (invalid/expired/already-used token) instead of bouncing the guest away.
          break;
        }
        this._toastrService.errToastr(error['error']['message']);
        localStorage.clear();
        this.router.navigate(['/auth/login']);
        break;
      }
      case responseStatus.REQUESTTIMEOUT: {
        this._toastrService.errToastr(
          isFileUpload ? uploadFallback() : error['error']['message']
        );
        break;
      }
      case responseStatus.INTERNALSERVERERROR:
        this._toastrService.errToastr(
          isFileUpload ? uploadFallback() : error['error']['message']
        );
        break;
      case responseStatus.RequestTookMoreThanExpected:
        this._toastrService.errToastr(error['error']['message']);
        break;
      case responseStatus.GatewayTimeOut:
        this._toastrService.errToastr(
          isFileUpload ? uploadFallback() : error['error']['message']
        );
        break;
      default:
        if (isFileUpload) {
          this._toastrService.errToastr(uploadFallback());
          break;
        }
        this._toastrService.errToastr(
          apiMessage || this.translateService.instant('networkConnection')
        );
    }
  }
}
